import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { categoryService } from '../../../services/categoryService';
import { accountService } from '../../../services/accountService';
import { transactionService } from '../../../services/transactionService';

const TransactionForm = ({ transaction, account, onSubmit, onCancel, preFillData, onPreFillDataUsed }) => {
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    type: 'expense',
    category: '',
    subcategory: '', // Add subcategory field
    cleared: false,
    paymentSourceAccountId: '', // For credit card payments
    transferAccountId: '' // For transfer transactions
  });

  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // For transfer account selection
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null); // Track selected parent
  const [availableSubcategories, setAvailableSubcategories] = useState([]); // Subcategories for selected parent
  const [transferAccountId, setTransferAccountId] = useState('');

  const isLiability = account?.group === 'Liabilities';
  const isAsset = account?.group === 'Assets';
  const isAssetPayment = isAsset && formData?.type === 'payment';
  
  // Add this block - Define isCreditCardPayment variable
  const isCreditCardPayment = isLiability && (formData?.type === 'payment' || formData?.type === 'income');
  
  // Check if selected subcategory is 'Transfer In' or 'Transfers Out'
  const isTransferInSubcategory = availableSubcategories?.find(
    sub => sub?.id === formData?.subcategory && sub?.name === 'Transfer In'
  );
  const isTransferOutSubcategory = availableSubcategories?.find(
    sub => sub?.id === formData?.subcategory && sub?.name === 'Transfers Out'
  );
  
  // Check if either Transfer In or Transfer Out subcategory is selected
  const isTransfer = isTransferInSubcategory || isTransferOutSubcategory;

  useEffect(() => {
    loadCategories();
    if (isLiability || isAsset) {
      loadAccounts();
    }
    // Load all accounts once on mount for transfer subcategories
    loadAllAccounts();
  }, [isLiability, isAsset]);

  useEffect(() => {
    if (transaction) {
      setFormData({
        date: transaction?.date?.split('T')?.[0] || '',
        description: transaction?.description || '',
        amount: Math.abs(transaction?.amount)?.toString() || '',
        type: transaction?.type || 'expense',
        category: transaction?.categoryId || '',
        subcategory: '',
        cleared: transaction?.cleared || false
      });
      
      // If transaction has a category, check if it's a subcategory
      if (transaction?.categoryId && categories?.length > 0) {
        const allCats = categories?.flatMap(parent => [parent, ...(parent?.subcategories || [])]);
        const selectedCat = allCats?.find(cat => cat?.id === transaction?.categoryId);
        
        if (selectedCat?.parentId) {
          // This is a subcategory
          setFormData(prev => ({
            ...prev,
            category: selectedCat?.parentId,
            subcategory: selectedCat?.id
          }));
          const parent = categories?.find(cat => cat?.id === selectedCat?.parentId);
          setSelectedParentCategory(parent);
          setAvailableSubcategories(parent?.subcategories || []);
        } else {
          // This is a parent category
          const parent = categories?.find(cat => cat?.id === transaction?.categoryId);
          setSelectedParentCategory(parent);
          setAvailableSubcategories(parent?.subcategories || []);
        }
      }
    }
  }, [transaction, categories]);

  // Separate useEffect for preFillData that depends on categories being loaded
  useEffect(() => {
    if (preFillData && categories?.length > 0) {
      // Pre-fill form with bill data
      setFormData({
        date: preFillData?.date?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
        description: preFillData?.description || '',
        amount: Math.abs(preFillData?.amount || 0)?.toString(),
        type: preFillData?.type || 'expense',
        category: '',
        subcategory: '',
        cleared: false,
        paymentSourceAccountId: '',
        transferAccountId: ''
      });
      
      // Set category and subcategory if provided
      if (preFillData?.categoryId) {
        const allCats = categories?.flatMap(parent => [parent, ...(parent?.subcategories || [])]);
        const selectedCat = allCats?.find(cat => cat?.id === preFillData?.categoryId);
        
        if (selectedCat) {
          if (selectedCat?.parentId) {
            // This is a subcategory
            setFormData(prev => ({
              ...prev,
              category: selectedCat?.parentId,
              subcategory: selectedCat?.id
            }));
            const parent = categories?.find(cat => cat?.id === selectedCat?.parentId);
            setSelectedParentCategory(parent);
            setAvailableSubcategories(parent?.subcategories || []);
          } else {
            // This is a parent category
            setFormData(prev => ({
              ...prev,
              category: selectedCat?.id
            }));
            setSelectedParentCategory(selectedCat);
            setAvailableSubcategories(selectedCat?.subcategories || []);
          }
        }
      }
      
      // Mark preFillData as used
      if (onPreFillDataUsed) {
        onPreFillDataUsed();
      }
    }
  }, [preFillData, categories, onPreFillDataUsed]);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await categoryService?.getHierarchical();
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService?.getAll();
      // For liability payments: show ALL Asset accounts and Cash accounts as payment sources
      // For asset payments: show Liability and Cash accounts as payment destinations
      let filteredAccounts;
      if (isLiability) {
        filteredAccounts = data?.filter(acc => 
          acc?.group === 'Assets' || acc?.group === 'Cash'
        );
      } else if (isAsset) {
        filteredAccounts = data?.filter(acc => 
          acc?.group === 'Liabilities' || acc?.group === 'Cash'
        );
      }
      setAccounts(filteredAccounts || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadAllAccounts = async () => {
    try {
      const data = await accountService?.getAll();
      // For transfers: show all accounts except the current account
      let filteredAccounts = data?.filter(acc => acc?.id !== account?.id);
      setAllAccounts(filteredAccounts || []);
    } catch (error) {
      console.error('Error loading all accounts:', error);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName?.trim()) return;

    try {
      setCreatingCategory(true);
      // Use the actual transaction type for category creation
      const newCategory = await categoryService?.create({
        name: newCategoryName?.trim(),
        type: formData?.type,
        color: '#6366f1',
        icon: 'Tag'
      });
      
      setCategories(prev => [...prev, newCategory]);
      setFormData(prev => ({ ...prev, category: newCategory?.id }));
      setNewCategoryName('');
      setShowNewCategoryForm(false);
    } catch (error) {
      console.error('Error creating category:', error);
      alert(error?.message || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const typeOptions = isLiability ? [
    { value: 'expense', label: 'Charge/Purchase' },
    { value: 'payment', label: 'Payment' }
  ] : [
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' },
    { value: 'payment', label: 'Payment' }
  ];

  // Get parent categories based on transaction type
  // For payment type, show payment categories for ALL account types
  // For credit card payments (liability income), show expense categories
  // For regular income/expense, show matching type categories
  const parentCategories = categories?.filter(cat => {
    if (!cat?.parentId) {
      // For payment type, show payment categories for ALL account types
      if (formData?.type === 'payment') {
        return cat?.type === 'payment';
      }
      // For credit card payments (liability income), show expense categories
      if (isLiability && formData?.type === 'income') {
        return cat?.type === 'expense';
      }
      // For income/expense, show matching type
      return cat?.type === formData?.type;
    }
    return false;
  });

  const categoryOptions = parentCategories?.map(cat => ({ 
    value: cat?.id, 
    label: cat?.emoji ? `${cat?.emoji} ${cat?.name}` : cat?.name 
  }));

  // Subcategory options based on selected parent category
  const subcategoryOptions = availableSubcategories?.map(sub => ({
    value: sub?.id,
    label: sub?.name
  }));

  const paymentSourceOptions = accounts?.map(acc => ({
    value: acc?.id,
    label: acc?.name
  }));

  const transferAccountOptions = allAccounts?.map(acc => ({
    value: acc?.id,
    label: `${acc?.name} (${acc?.type})`
  }));

  // Handle category selection
  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({ ...prev, category: categoryId, subcategory: '' }));
    
    // Find the selected parent category and set available subcategories
    const parent = categories?.find(cat => cat?.id === categoryId);
    setSelectedParentCategory(parent);
    setAvailableSubcategories(parent?.subcategories || []);
  };

  // Handle subcategory selection
  const handleSubcategoryChange = (subcategoryId) => {
    setFormData(prev => ({ ...prev, subcategory: subcategoryId }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const amount = parseFloat(formData?.amount);
    let finalAmount;
    
    // Determine the final category ID (use subcategory if selected, otherwise parent)
    const finalCategoryId = formData?.subcategory || formData?.category;

    if (isLiability) {
      // For credit cards/liabilities:
      // - Expenses (charges) INCREASE the debt → positive
      // - Income (payments) DECREASE the debt → negative
      // - Payment type DECREASES the debt → negative
      if (formData?.type === 'expense') {
        finalAmount = Math.abs(amount);
      } else {
        finalAmount = -Math.abs(amount);
      }
    } else {
      // For asset/cash accounts:
      // - Expenses DECREASE the balance → negative
      // - Income INCREASES the balance → positive
      // - Payments DECREASE the balance → negative
      if (formData?.type === 'expense' || formData?.type === 'payment') {
        finalAmount = -Math.abs(amount);
      } else {
        finalAmount = Math.abs(amount);
      }
    }

    // CRITICAL FIX: Convert 'payment' type to 'expense' for database
    // 'payment' is a UI-only label, database only accepts: income, expense, transfer
    const dbTransactionType = formData?.type === 'payment' ? 'expense' : formData?.type;

    const submitData = {
      accountId: account?.id,
      categoryId: finalCategoryId,
      date: new Date(formData?.date)?.toISOString(),
      description: formData?.description,
      amount: finalAmount,
      type: dbTransactionType, // Use converted type
      cleared: formData?.cleared
    };

    // Handle matching transactions for Payment type
    if (formData?.type === 'payment' && formData?.paymentSourceAccountId) {
      try {
        // Create payment transaction on current account
        await onSubmit(submitData);

        // Determine matching transaction details based on account type
        let matchingAmount;
        let matchingType;
        
        if (isLiability) {
          // For liability: payment reduces debt (negative), matching expense on source account (negative)
          matchingAmount = -Math.abs(amount);
          matchingType = 'expense';
        } else {
          // For asset/cash: payment reduces balance (negative), matching expense on destination (negative)
          matchingAmount = -Math.abs(amount);
          matchingType = 'expense';
        }

        // Use the same category/subcategory as the payment transaction
        const finalCategoryId = formData?.subcategory || formData?.category;

        // Create matching transaction on source/destination account
        const matchingTransaction = {
          accountId: formData?.paymentSourceAccountId,
          categoryId: finalCategoryId,
          date: formData?.date,
          description: isLiability 
            ? `Payment to ${account?.name}` 
            : `Payment from ${account?.name}`,
          amount: matchingAmount,
          type: matchingType,
          cleared: formData?.cleared
        };

        await transactionService?.create(matchingTransaction);
        alert('Payment transaction created successfully with matching entry!');
      } catch (error) {
        console.error('Error creating matching payment transaction:', error);
        alert('Payment created on current account, but failed to record on destination account');
      }
      return;
    }

    if (isLiability && formData?.paymentSourceAccountId) {
      // If this is a credit card payment with a source account, create matching transaction
      try {
        // Create payment on credit card (negative = reduces debt)
        await onSubmit(submitData);
        
        // Create matching expense on the source account (negative = reduces balance)
        const destinationAccount = accounts?.find(acc => acc?.id === formData?.paymentSourceAccountId);
        const isDestinationLiability = destinationAccount?.group === 'Liabilities';

        // For liability destination: payment reduces debt (negative)
        // For cash destination: payment increases balance (positive)
        const destinationAmount = isDestinationLiability ? -Math.abs(amount) : Math.abs(amount);
        const destinationType = isDestinationLiability ? 'income' : 'income';

        // Use the same category/subcategory as the payment transaction
        const finalCategoryId = formData?.subcategory || formData?.category;

        await transactionService?.create({
          accountId: formData?.paymentSourceAccountId,
          categoryId: finalCategoryId,
          date: new Date(formData?.date)?.toISOString(),
          description: `Payment to ${account?.name}`,
          amount: -Math.abs(amount), // Always negative for source account
          type: 'expense',
          cleared: formData?.cleared
        });
        
        alert(`Payment recorded on both ${account?.name} and ${destinationAccount?.name}`);
      } catch (error) {
        console.error('Error creating matching transaction:', error);
        alert('Payment created on asset account, but failed to record on destination account');
      }
    } else if (isTransferOutSubcategory && transferAccountId) {
      // If 'Transfers Out' subcategory is selected with an account, create corresponding transaction
      try {
        // Create transaction on current account (expense - negative)
        await onSubmit(submitData);
        
        // Find Transfer In subcategory for the matching transaction
        const allCategories = await categoryService?.getHierarchical();
        const transfersOtherCategory = allCategories?.find(
          cat => cat?.name === 'Transfers & Other' && cat?.type === 'income'
        );
        const transferInSubcategory = transfersOtherCategory?.subcategories?.find(
          sub => sub?.name === 'Transfer In'
        );
        
        if (transferInSubcategory) {
          await transactionService?.create({
            accountId: transferAccountId,
            categoryId: transferInSubcategory?.id,
            date: new Date(formData?.date)?.toISOString(),
            description: `Transfer from ${account?.name}`,
            amount: Math.abs(amount),
            type: 'income',
            cleared: formData?.cleared
          });
        }
        
        const destinationAccount = allAccounts?.find(acc => acc?.id === transferAccountId);
        alert(`Transfer recorded on both ${account?.name} and ${destinationAccount?.name}`);
      } catch (error) {
        console.error('Error creating matching transfer transaction:', error);
        alert('Transfer created on current account, but failed to record on destination account');
      }
    } else if (isTransferInSubcategory && transferAccountId) {
      // If 'Transfer In' subcategory is selected with an account, create corresponding transaction
      try {
        // Create transaction on current account (income - positive)
        await onSubmit(submitData);
        
        // Find Transfers Out subcategory for the matching transaction
        const allCategories = await categoryService?.getHierarchical();
        const transfersAdjustmentsCategory = allCategories?.find(
          cat => cat?.name === 'Transfers & Adjustments' && cat?.type === 'expense'
        );
        const transferOutSubcategory = transfersAdjustmentsCategory?.subcategories?.find(
          sub => sub?.name === 'Transfers Out'
        );
        
        if (transferOutSubcategory) {
          await transactionService?.create({
            accountId: transferAccountId,
            categoryId: transferOutSubcategory?.id,
            date: new Date(formData?.date)?.toISOString(),
            description: `Transfer to ${account?.name}`,
            amount: -Math.abs(amount),
            type: 'expense',
            cleared: formData?.cleared
          });
        }
        
        const sourceAccount = allAccounts?.find(acc => acc?.id === transferAccountId);
        alert(`Transfer recorded on both ${account?.name} and ${sourceAccount?.name}`);
      } catch (error) {
        console.error('Error creating matching transfer transaction:', error);
        alert('Transfer created on current account, but failed to record on source account');
      }
    } else {
      onSubmit(submitData);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'category' && value === '__create_new__') {
      setShowNewCategoryForm(true);
      return;
    }
    
    // When type changes, reset category and subcategory selections
    if (field === 'type') {
      setFormData(prev => ({ ...prev, [field]: value, category: '', subcategory: '' }));
      setSelectedParentCategory(null);
      setAvailableSubcategories([]);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-16">
      {isLiability && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-16 mb-16">
          <div className="flex items-start gap-12">
            <div className="text-blue-600 mt-2">ℹ️</div>
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-4">Liability Account</p>
              <ul className="space-y-2 list-disc list-inside text-xs">
                <li><strong>Charges/Purchases</strong> increase your balance (amount owed)</li>
                <li><strong>Payments</strong> decrease your balance (debt paid down)</li>
                {isCreditCardPayment && (
                  <li className="text-blue-700 font-medium">Select which account the payment comes from below</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {isAssetPayment && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-16 mb-16">
          <div className="flex items-start gap-12">
            <div className="text-green-600 mt-2">ℹ️</div>
            <div className="text-sm text-green-900">
              <p className="font-medium mb-4">Asset Payment</p>
              <ul className="space-y-2 list-disc list-inside text-xs">
                <li><strong>Payment</strong> decreases your asset balance (money going out)</li>
                <li className="text-green-700 font-medium">Select which account receives the payment below</li>
                <li>Can pay to liability accounts (reduces debt) or cash accounts</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Date *
          </label>
          <Input
            type="date"
            value={formData?.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e?.target?.value }))}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Type *
          </label>
          <Select
            options={typeOptions}
            value={formData?.type}
            onChange={(value) => {
              setFormData(prev => ({ ...prev, type: value, category: '', subcategory: '' }));
              setSelectedParentCategory(null);
              setAvailableSubcategories([]);
            }}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-caption font-medium text-foreground mb-8">
          Description *
        </label>
        <Input
          type="text"
          value={formData?.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
          placeholder="Enter transaction description"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Amount ({account?.currency || 'USD'}) *
          </label>
          <Input
            type="number"
            step="0.01"
            value={formData?.amount}
            onChange={(e) => setFormData(prev => ({ ...prev, amount: e?.target?.value }))}
            placeholder="0.00"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Category *
          </label>
          {loadingCategories ? (
            <div className="text-sm text-muted-foreground">Loading categories...</div>
          ) : (
            <Select
              options={categoryOptions}
              value={formData?.category}
              onChange={handleCategoryChange}
              placeholder="Select a category"
              required
            />
          )}
        </div>
      </div>

      {/* Subcategory dropdown - only show if parent category is selected and has subcategories */}
      {formData?.category && availableSubcategories?.length > 0 && (
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Subcategory (Optional)
          </label>
          <Select
            options={subcategoryOptions}
            value={formData?.subcategory}
            onChange={handleSubcategoryChange}
            placeholder="Select a subcategory (optional)"
          />
        </div>
      )}

      {isCreditCardPayment && (
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Payment Source Account (Optional)
          </label>
          <Select
            options={paymentSourceOptions}
            value={formData?.paymentSourceAccountId}
            onChange={(value) => handleChange('paymentSourceAccountId', value)}
            placeholder="Select payment source account"
          />
          <p className="text-xs text-muted-foreground mt-4">
            Select the account you're paying from (optional)
          </p>
        </div>
      )}

      {isAssetPayment && (
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Payment Destination Account
          </label>
          <Select
            options={paymentSourceOptions}
            value={formData?.paymentSourceAccountId}
            onChange={(value) => handleChange('paymentSourceAccountId', value)}
            placeholder="Select account to pay to (optional)"
          />
          <p className="text-xs text-muted-foreground mt-4">
            Optional: A matching transaction will be created on the selected account if provided
          </p>
        </div>
      )}

      {/* Show Transfer Account selector for Transfers Out subcategory */}
      {isTransferOutSubcategory && (
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Transfer To Account (Optional)
          </label>
          <Select
            options={transferAccountOptions}
            value={transferAccountId}
            onChange={(value) => setTransferAccountId(value)}
            placeholder="Select destination account"
          />
          <p className="text-xs text-muted-foreground mt-4">
            Optional: Select an account to automatically create a corresponding Transfer In transaction
          </p>
        </div>
      )}

      {/* Show Transfer Account selector for Transfer In subcategory */}
      {isTransferInSubcategory && (
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Transfer From Account (Optional)
          </label>
          <Select
            options={transferAccountOptions}
            value={transferAccountId}
            onChange={(value) => setTransferAccountId(value)}
            placeholder="Select source account"
          />
          <p className="text-xs text-muted-foreground mt-4">
            Optional: Select an account to automatically create a corresponding Transfer Out transaction
          </p>
        </div>
      )}

      {showNewCategoryForm && (
        <div className="p-16 bg-muted rounded-lg space-y-12">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">Create New Category</h4>
            <button
              type="button"
              onClick={() => {
                setShowNewCategoryForm(false);
                setNewCategoryName('');
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-8">
            <Input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e?.target?.value)}
              placeholder="Category name"
              className="flex-1"
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleCreateCategory}
              disabled={!newCategoryName?.trim() || creatingCategory}
            >
              {creatingCategory ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      <div>
        <label className="flex items-center gap-8 cursor-pointer">
          <Checkbox
            checked={formData?.cleared}
            onChange={(e) => handleChange('cleared', e?.target?.checked)}
          />
          <span className="text-sm font-caption font-medium text-foreground">
            Mark as cleared
          </span>
        </label>
        <p className="text-xs text-muted-foreground mt-4 ml-24">
          Cleared transactions have been verified and reconciled
        </p>
      </div>

      <div className="flex justify-end gap-12">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
        >
          {transaction ? 'Update' : 'Add'} Transaction
        </Button>
      </div>
    </form>
  );
};

export default TransactionForm;